
import * as React from 'react';
import { IComponentDecorator, IDecoratedProps } from '../../../utilities/components/Decorators';
import { DetailsList, IDetailsListProps, IDetailsRowProps, DetailsRow, IColumn } from 'office-ui-fabric-react/lib/DetailsList';
import { ShimmeredDetailsList } from 'office-ui-fabric-react/lib/ShimmeredDetailsList';
import { Shimmer } from '@uifabric/experiments/lib/Shimmer';
import { IItemCache } from '../../../models/IItemCacheObserver';
import { connectToItemCache } from '../../decorators/connectToItemCache';
import { enableShimmeredDetailsListConfigurationFacet } from '../../../facets/ConfigurationFacets';
import { getConfiguration } from '../../../models/configuration/getConfiguration';

export interface IShimmeredDetailsListProps {
  detailsListAs: React.ComponentType<IDetailsListProps>;
}

/**
 * Decorator which applies shimmer around the details list.
 */
export function withDetailsListShimmer(): IComponentDecorator<IShimmeredDetailsListProps, {}, IDetailsListProps> {
  return <P extends IDetailsListProps & {
    detailsListAs: React.ComponentType<IDetailsListProps>;
  }>(
    component: React.ComponentType<P>
  ): React.ComponentType<IDecoratedProps<P, IShimmeredDetailsListProps, {}>> => {
    function AsDetailsListShimmer(props: IDetailsListProps): JSX.Element {
      return (
        <ShimmeredDetailsList
          { ...props }
          enableShimmer={ !!props.enableShimmer && props.items.length === 0 }
        />
      );
    }

    function WithShimmerRows(props: IDetailsListProps): JSX.Element {
      return (
        <DetailsList
          { ...props }
          onRenderMissingItem={ renderMissingItem }
          columns={ createShimmerColumns(props.columns, props.enableShimmer) }
        />
      );
    }

    const WithDetailsListShimmer = connectToItemCache((itemCache: IItemCache): IShimmeredDetailsListProps => {
      const {
        enableShimmeredDetailsList = true
      } = getConfiguration(itemCache, {
        enableShimmeredDetailsList: enableShimmeredDetailsListConfigurationFacet
      });

      return {
        detailsListAs: enableShimmeredDetailsList ? AsDetailsListShimmer : WithShimmerRows
      };
    })(component);

    WithDetailsListShimmer.displayName = 'WithDetailsListShimmer';

    return WithDetailsListShimmer as {} as React.ComponentType<IDecoratedProps<P, IShimmeredDetailsListProps, {}>>;
  };
}

/**
 * Render function to run on missing items of the final array passed to DetailsList.
 */
function renderMissingItem(
  index: number | undefined,
  rowProps: IDetailsRowProps | undefined
): React.ReactNode {
  if (!rowProps) {
    return null;
  }

  return (
    <Shimmer
      isBaseStyle={ true }
    >
      <DetailsRow
        { ...rowProps }
        shimmer={ true }
      />
    </Shimmer>
  );
}

function createShimmerColumns(columns?: IColumn[], enableShimmer?: boolean): IColumn[] | undefined {
  return enableShimmer && columns ? columns.map((column: IColumn): IColumn => {
    return {
      ...column,
      key: `${column.key}-shimmer`
    };
  }) : columns;
}
